import { useCallback, useEffect, useRef } from 'react';
import { adApi } from '../api/ad.api';
import { AdPosition, AdEventType } from '../types/Ad.api.type';

/**
 * 광고 이벤트 기록 훅
 * - IntersectionObserver로 노출(IMPRESSION) 자동 감지
 * - 클릭(CLICK), 닫기(DISMISS) 수동 호출
 */
export const useAdEvent = (adId: number | undefined, position: AdPosition) => {
  const impressionSent = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const elementRef = useRef<HTMLDivElement | null>(null);

  /** 이벤트 기록 (silent fail) */
  const sendEvent = useCallback(
    (eventType: AdEventType) => {
      if (!adId) return;
      adApi.recordEvent({ adId, eventType, position, platform: 'WEB' }).catch(() => {
        // silent fail: 이벤트 기록 실패가 사용자 경험에 영향 없음
      });
    },
    [adId, position]
  );

  /** 노출 감지용 ref 콜백 */
  const impressionRef = useCallback(
    (node: HTMLDivElement | null) => {
      // 기존 observer 정리
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      if (!node || !adId || impressionSent.current) return;

      elementRef.current = node;
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting && !impressionSent.current) {
            impressionSent.current = true;
            sendEvent('IMPRESSION');
          }
        },
        { threshold: 0.5 }
      );
      observerRef.current.observe(node);
    },
    [adId, sendEvent]
  );

  /** 클릭 핸들러 */
  const handleClick = useCallback(() => {
    sendEvent('CLICK');
  }, [sendEvent]);

  /** 닫기 핸들러 */
  const handleDismiss = useCallback(() => {
    sendEvent('DISMISS');
  }, [sendEvent]);

  // 컴포넌트 언마운트 시 observer 정리
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return {
    impressionRef,
    handleClick,
    handleDismiss,
  };
};
